import * as React from 'react';
import { clsx } from 'clsx';

import * as styles from './RecommendationOffer.css';
import { Carousel } from '../Carousel';
import { UIHeading2, UIText2 } from '@cian/ui-kit';
import { WithHover } from '../../hocs/WithHover';
import { formatOfferPrice } from '../../utils/formatOfferPrice';
import { addressToString } from '../../utils/formatters/address';
import { LinkArea } from '../LinkArea';
import { TRecommendation } from '../../types/recommendation';

interface IRecommendationOfferProps {
  offer: TRecommendation;
  geoInformation: React.ReactNode;
  isMobile: boolean;
  isHovered: boolean;
  position: number;
  labels: React.ReactNode;
  pageTypeSource?: string | null;
  onClick(offer: TRecommendation, position: number): void;
}

export const RecommendationOffer = ({
  position,
  geoInformation,
  isMobile,
  isHovered,
  offer,
  labels,
  pageTypeSource,
  onClick,
}: IRecommendationOfferProps): React.JSX.Element => {
  const {
    url,
    photos = [],
    price,
    isWithTransport,
    address,
    info,
  } = React.useMemo(() => {
    const { photos, url, geo, info } = offer;

    const price = formatOfferPrice(offer);
    const address = addressToString(geo.address);

    const isWithTransport = !!geo.highways?.length || !!geo.railways?.length || !!geo.undergrounds?.length;

    return {
      photos,
      url,
      price,
      address,
      info,
      isWithTransport,
    };
  }, [offer]);

  const handleClick = React.useCallback(() => onClick(offer, position), [offer, position, onClick]);

  const urlSource = pageTypeSource === 'chat_bot' ? '?source=chat_bot' : '';

  return (
    <div className={styles['offer']} data-testid="RecommendationOffer">
      <a
        className={styles['gallery']}
        onClick={handleClick}
        href={`${url}${urlSource}`}
        target="_blank"
        rel="noreferrer"
      >
        <Carousel media={photos} desktopControls={isHovered && !isMobile} />
      </a>

      <LinkArea
        onClick={handleClick}
        className={styles['info']}
        href={`${url}${urlSource}`}
        target="_blank"
        rel="noreferrer"
      >
        <div className={styles['price']}>
          <UIHeading2>{price}</UIHeading2>
        </div>
        <a className={styles['link']} href={`${url}${urlSource}`} target="_blank" rel="noreferrer">
          {info}
        </a>
        {geoInformation}
        <div className={clsx(isWithTransport && styles['one-line-text'])}>
          <UIText2 color="black_60">{address}</UIText2>
        </div>
      </LinkArea>
      {labels}
    </div>
  );
};

export const RecommendationOfferWithHover = WithHover(RecommendationOffer);
